/**
 * Highlights a BPMN diagram element by setting its stroke color.
 *
 * @param {string} elementId - The ID of the element to highlight.
 * @param {string} color - The color to use for highlighting (e.g., '#FF0000').
 * @param {Object} viewer - The BPMN viewer instance.
 */
export function highlightElement(elementId, color, viewer) {
    const modeling = viewer.get('modeling');
    const elementRegistry = viewer.get('elementRegistry');
  
    // Retrieve the element by its ID
    const element = elementRegistry.get(elementId);
  
    if (element) {
      // Apply the color using the modeling service
      modeling.setColor(element, { stroke: color });
    } else {
      console.error(`Element with ID ${elementId} not found.`);
    }
}


/**
 * Clears highlights from all elements in the BPMN diagram.
 * 
 * For message flows (bpmn:MessageFlow), resets the stroke color to the default gray (#999999).
 * For all other elements, removes any custom stroke color (sets to null).
 *
 * @param {Object} viewer - The BPMN viewer instance.
 */
export function clearHighlights(viewer) {
    const modeling = viewer.get('modeling');
    const elementRegistry = viewer.get('elementRegistry');
  
    // Iterate over all elements in the diagram
    elementRegistry.forEach((element) => {
      if (element.businessObject && element.businessObject.id) {
        const bo = element.businessObject;
  
        // Check if the element is a MessageFlow
        if (bo.$type === 'bpmn:MessageFlow') {
          // Set default border color for message flows
          modeling.setColor(element, { stroke: '#999999' });
        } else {
          // Reset color for all other elements
          modeling.setColor(element, { stroke: null });
        }
      }
    });
}
  

/**
 * Retrieves the display name of a BPMN element by its ID.
 * 
 * For SequenceFlow and Association elements, returns a string in the format "source → target".
 * For other elements, returns the element's name or '[Unnamed Element]' if not set.
 *
 * @param {string} elementId - The ID of the BPMN element.
 * @param {Object} viewer - The BPMN viewer instance.
 * @returns {string|null} The name of the element, or null if not found.
 */
export function getElementNameById(elementId, viewer) {
    const elementRegistry = viewer.get('elementRegistry');
  
    // Retrieve the element by ID
    const element = elementRegistry.get(elementId);
  
    if (!element) {
      console.error(`Element with ID ${elementId} not found.`);
      return null;
    }
  
    const businessObject = element.businessObject;
  
    if (businessObject.$type === 'bpmn:SequenceFlow' || businessObject.$type === 'bpmn:Association') {
      // Construct name for connections (source → target)
      const sourceName = businessObject.sourceRef?.name || '[Unnamed Source]';
      const targetName = businessObject.targetRef?.name || '[Unnamed Target]';
      return `${sourceName} → ${targetName}`;
    } else {
      // Return name for other elements
      return businessObject.name || '[Unnamed Element]';
    }
}